import { useMemo } from "react"

import { useAPIHealth } from "shared/api/health/useAPIHealth"
import { useEvent } from "shared/lib/useEvent"
import { Floor } from "shared/types/Floor"
import { FloorTypename } from "shared/types/Project"

import { useUpdateAttachmentLinks } from "entities/attachments/api/useUpdateAttachmentLinks"
import { useFloorMutations } from "entities/floors/model/useFloorMutations"
import { getProductTotal } from "entities/projects/lib/getProductTotal"
import { useProjectChildPosition } from "entities/projects/model/useProjectChildPosition"
import { useProjectRequestMonitor } from "entities/projects/model/useProjectRequestState"
import { useFloor } from "features/floor-management/model/useFloor"
import { FloorFormData } from "features/floor-management/types/FloorFormData"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { useFloorForm } from "widgets/floor/floor-form/model/useFloorForm"

import { ProjectPageFloorSidebarModel } from "../ProjectPageFloorSidebar"

export interface UseProjectPageFloorSidebarModelArgs {
  projectID: string
  floor: Floor
}

export function useProjectPageFloorSidebarModel({
  projectID,
  floor
}: UseProjectPageFloorSidebarModelArgs): ProjectPageFloorSidebarModel {
  const {
    pendingElementIDs,
    sidebarIsEditing,
    sidebarActiveTab,
    enterSidebarEditing,
    exitSidebarEditing,
    setSidebarActiveTab
  } = useProjectStore(projectID)
  const isPending = pendingElementIDs.includes(floor.id)

  const { price: isPriceServiceAvailable } = useAPIHealth()

  const { isUpdatingChildren } = useProjectRequestMonitor({ projectID })

  const { updateFloor: updateFloorMutation } = useFloorMutations()
  const [updateFloor] = updateFloorMutation
  const updateAttachmentLinks = useUpdateAttachmentLinks()

  const floorModel = useFloor({
    floorID: floor.id,
    projectID,
    options: { includePrice: true }
  })

  const { displayPosition } = useProjectChildPosition({
    child: floor,
    projectID
  })

  const handleSubmit = useEvent(async (floorFormData: FloorFormData) => {
    floorModel.clarificationRequest?.handlers.onSubmitContent?.(
      floorFormData.clarificationRequest?.content
    )
    const results = await Promise.all([
      updateFloor({
        projectID,
        displayPosition: floorFormData.displayPosition,
        floor: { ...floorFormData.floor, id: floor.id }
      }),
      floorFormData.attachments
        ? updateAttachmentLinks({
            id: floor.id,
            resourceType: FloorTypename,
            attachmentLinks: floorFormData.attachments
          })
        : Promise.resolve({ data: {}, errors: undefined })
    ])
    const errors = results.flatMap(r => r?.errors ?? [])
    return { data: {}, errors: errors.length ? errors : undefined }
  })

  const floorFormModel = useFloorForm({
    floor,
    projectID,
    parentID: projectID,
    displayPosition,
    model: floorModel,
    onSubmit: handleSubmit
  })

  const productTotal = useMemo(
    () => getProductTotal(floor.children),
    [floor.children]
  )

  const { data: priceData, isLoading: isLoadingPrice = false } =
    floorModel.pricing.data.price ?? {}

  return {
    projectID,
    floor,
    price: isPriceServiceAvailable ? priceData : null,
    isLoadingPrice: isPriceServiceAvailable ? isLoadingPrice : false,
    isPriceServiceAvailable,
    productTotal,
    isPending,
    isDisabled: isUpdatingChildren,
    floorFormModel,
    callbacks: floorModel.core.handlers,
    hideControls: !floorModel.core.handlers.onEdit,
    isEditing: sidebarIsEditing,
    enterEditing: enterSidebarEditing,
    exitEditing: exitSidebarEditing,
    sidebarActiveTab,
    setSidebarActiveTab
  }
}
